// src/pages/Admin.jsx
import React, { useState, useEffect } from "react";
import "./admin.css";
import { 
  getProducts, 
  createProduct, 
  updateProduct, 
  deleteProduct, 
  adminLogin, 
  getProductVariants, 
  updateProductVariant 
} from "../../services/api";

const Admin = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [products, setProducts] = useState([]);
  const [modalType, setModalType] = useState(null); // "edit", "delete" ou "new"
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("admin-token");
    if (token) {
      setLoggedIn(true);
      loadProducts();
    }
  }, []);

  const loadProducts = () => {
    getProducts()
      .then((data) => setProducts(data))
      .catch((err) => console.error("Erro ao carregar produtos:", err));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(username, password);
      if (res.success) {
        localStorage.setItem("admin-token", res.token);
        setLoggedIn(true);
        loadProducts();
      } else {
        setLoginError("Credenciais inválidas");
      }
    } catch (error) {
      setLoginError("Erro ao fazer login");
      console.error(error);
    }
  };

  // Inicia o modal para criação de novo produto
  const handleNewProduct = () => {
    setSelectedProduct({
      titulo: "",
      descricao: "",
      descricaoLonga: ""
    });
    setSelectedVariants([]);
    setModalType("new");
  };

  // Ao editar, carrega os dados do produto e suas variantes
  const handleEdit = async (product) => {
    setSelectedProduct({ ...product });
    setModalType("edit");
    try {
      const variants = await getProductVariants(product.id);
      setSelectedVariants(variants);
    } catch (error) {
      console.error("Erro ao carregar variantes:", error);
      setSelectedVariants([]);
    }
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedProduct(null);
    setSelectedVariants([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Atualiza qualquer campo da variante (preço, estoque ou imagens)
  const handleVariantFieldChange = (e, variantId) => {
    const { name, value } = e.target;
    setSelectedVariants((prev) =>
      prev.map((variant) =>
        variant.id === variantId ? { ...variant, [name]: value } : variant
      )
    );
  };

  const handleSaveEdit = async () => {
    try {
      const updatedData = {
        titulo: selectedProduct.titulo,
        descricao: selectedProduct.descricao,
        descricaoLonga: selectedProduct.descricaoLonga
      };
      await updateProduct(selectedProduct.id, updatedData);

      for (const variant of selectedVariants) {
        await updateProductVariant(selectedProduct.id, variant.id, { 
          preco: variant.preco, 
          estoque: variant.estoque,
          imagem_url: variant.imagem_url,
          imagem_url2: variant.imagem_url2
        });
      }

      loadProducts();
      closeModal();
    } catch (error) {
      console.error("Erro ao atualizar produto e variantes:", error);
    }
  };

  const handleSaveNew = async () => {
    try {
      await createProduct({ ...selectedProduct, variants: selectedVariants });
      loadProducts();
      closeModal();
    } catch (error) {
      console.error("Erro ao criar produto:", error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteProduct(selectedProduct.id);
      loadProducts();
      closeModal();
    } catch (error) {
      console.error("Erro ao excluir produto:", error);
    }
  };

  if (!loggedIn) {
    return (
      <div className="admin-page">
        <div className="login-container">
          <h1 className="section-title">Admin Login</h1>
          <form onSubmit={handleLogin} className="login-form">
            <label>
              Usuário:
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>
            <label>
              Senha:
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {loginError && <p className="login-error">{loginError}</p>}
            <button type="submit" className="login-button">Login</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="header-actions">
          <button onClick={handleNewProduct} className="new-product-button">
            +
          </button>
        </div>
        <h1 className="title-t">Produtos</h1>
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID Produto</th>
                <th>Nome</th>
                <th>Imagem</th>
                <th>Quantidade</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {products.map((prod) => {
                const totalEstoque =
                  prod.variants && prod.variants.length > 0
                    ? prod.variants.reduce((sum, variant) => sum + Number(variant.estoque || 0), 0)
                    : prod.quantidade;
                return (
                  <tr key={prod.id}>
                    <td>{prod.id}</td>
                    <td>{prod.titulo}</td>
                    <td>
                      <img src={prod.imagem_url} alt={prod.titulo} />
                    </td>
                    <td>{totalEstoque}</td>
                    <td>
                      <button
                        className="action-button"
                        onClick={() => handleEdit(prod)}
                      >
                        Editar
                      </button>
                      <button
                        className="action-button delete"
                        onClick={() => handleDelete(prod)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {(modalType === "edit" || modalType === "new") && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{modalType === "new" ? "Novo Produto" : "Editar Produto"}</h2>
            <form>
              <label>
                Nome:
                <input
                  type="text"
                  name="titulo"
                  value={selectedProduct.titulo}
                  onChange={handleInputChange}
                />
              </label>
              <label>
                Descrição:
                <input
                  type="text"
                  name="descricao"
                  value={selectedProduct.descricao}
                  onChange={handleInputChange}
                />
              </label>
              <label>
                Descrição Longa:
                <textarea
                  name="descricaoLonga"
                  value={selectedProduct.descricaoLonga}
                  onChange={handleInputChange}
                />
              </label>
              {/* Os inputs antigos de imagem do produto foram removidos */}
              <h3>Variantes</h3>
              {selectedVariants.map((variant, index) => (
                <div key={variant.id || index} className="variant-item">
                  <label>
                    Tamanho:
                    <input type="text" value={variant.tamanho} readOnly />
                  </label>
                  <label>
                    Preço:
                    <input
                      type="number"
                      name="preco"
                      value={variant.preco}
                      onChange={(e) => handleVariantFieldChange(e, variant.id)}
                    />
                  </label>
                  <label>
                    Estoque:
                    <input
                      type="number"
                      name="estoque"
                      value={variant.estoque}
                      onChange={(e) => handleVariantFieldChange(e, variant.id)}
                    />
                  </label>
                  <label>
                    Imagem Principal da Variante:
                    <input
                      type="text"
                      name="imagem_url"
                      value={variant.imagem_url || ""}
                      onChange={(e) => handleVariantFieldChange(e, variant.id)}
                    />
                  </label>
                  <label>
                    Segunda Imagem da Variante:
                    <input
                      type="text"
                      name="imagem_url2"
                      value={variant.imagem_url2 || ""}
                      onChange={(e) => handleVariantFieldChange(e, variant.id)}
                    />
                  </label>
                </div>
              ))}
              {modalType === "new" && (
                <div className="add-variant">
                  <h4>Adicionar Variante</h4>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedVariants((prev) => [
                        ...prev,
                        { tamanho: "", preco: 0, estoque: 0, imagem_url: "", imagem_url2: "" },
                      ])
                    }
                    className="modal-button add-variant-button"
                  >
                    Adicionar Variante
                  </button>
                </div>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={modalType === "new" ? handleSaveNew : handleSaveEdit}
                  className="modal-button save-button"
                >
                  Salvar
                </button>
                <button type="button" onClick={closeModal} className="modal-button cancel-button">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalType === "delete" && selectedProduct && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Excluir Produto</h2>
            <p>
              Tem certeza que deseja excluir o produto{" "}
              <strong>{selectedProduct.titulo}</strong>?
            </p>
            <div className="modal-actions">
              <button type="button" onClick={handleConfirmDelete} className="modal-button delete-button">
                Confirmar
              </button>
              <button type="button" onClick={closeModal} className="modal-button cancel-button">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
